import { useCallback, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { EmailContentOverride, EmailLayoutOverride } from '@/services/emailTemplates';

/** Language codes are optional on every template row; a null one applies to any language. */
export const ANY_LANGUAGE = '';

/** Which row a save writes to. "global" is read-only — it is source-controlled in bygr-database. */
export type EditScope = 'global' | 'profile' | 'project';

/** What the whole page is about: the scope being viewed and edited. */
export interface ScopeState {
    projectId: number | null;
    projectName: string | null;
    profileId: number | null;
    profileName: string | null;
    languageCode: string | null;
}

/**
 * Display names stay out of the URL, cached here by id. Module-level rather than hook state so every
 * instance of useScopeInUrl — the section page's bar and the tab views each have one — shows the same
 * labels: a name learned by one (a picker selection, or useScopeNamesFromResolution naming a deep-linked
 * id) is immediately visible to all. Keying by id also means a name can never describe the wrong row
 * after back/forward navigation.
 */
const projectNames = new Map<number, string>();
const profileNames = new Map<number, string>();

/**
 * Scope selections live in the query string (ids and language only), so a refresh keeps them and a link
 * can carry them. The URL is the single source of truth; with nothing in it the scope is empty and the
 * read-only global template is what is shown. Updates replace the history entry rather than pushing, so
 * Back leaves the page instead of stepping through every picker change.
 *
 * The scope is recomputed every render (no memo): name-cache writes re-render via the history.replace
 * they ride along with, and the parse is trivially cheap.
 */
export function useScopeInUrl(): [ScopeState, (scope: ScopeState) => void] {
    const history = useHistory();
    const location = useLocation();

    const params = new URLSearchParams(location.search);
    const id = (key: string) => {
        const value = Number(params.get(key));
        return Number.isInteger(value) && value > 0 ? value : null;
    };
    const projectId = id('projectId');
    const profileId = id('profileId');
    const scope: ScopeState = {
        projectId,
        projectName: projectId != null ? (projectNames.get(projectId) ?? null) : null,
        profileId,
        profileName: profileId != null ? (profileNames.get(profileId) ?? null) : null,
        languageCode: params.get('language') || null,
    };

    const setScope = useCallback(
        (next: ScopeState) => {
            if (next.projectId != null && next.projectName) {
                projectNames.set(next.projectId, next.projectName);
            }
            if (next.profileId != null && next.profileName) {
                profileNames.set(next.profileId, next.profileName);
            }

            const nextParams = new URLSearchParams();
            if (next.profileId != null) {
                nextParams.set('profileId', String(next.profileId));
            }
            if (next.projectId != null) {
                nextParams.set('projectId', String(next.projectId));
            }
            if (next.languageCode) {
                nextParams.set('language', next.languageCode);
            }
            history.replace({ pathname: location.pathname, search: nextParams.toString() });
        },
        [history, location.pathname]
    );

    return [scope, setScope];
}

/**
 * Fills in the picker labels for ids that arrived via the URL: once the resolution response names them,
 * the scope is updated — and when a deep link carries only a project, the owning profile comes along too,
 * exactly as picking that project by hand would have set it.
 */
export function useScopeNamesFromResolution(
    scope: ScopeState,
    setScope: (scope: ScopeState) => void,
    resolution: { projectId?: number | null; projectName?: string | null; profileId?: number | null; profileName?: string | null } | null
): void {
    useEffect(() => {
        if (!resolution) {
            return;
        }

        // Guard on ids so a stale resolution (still loading after a scope change) cannot mislabel.
        const nameProject =
            scope.projectId != null && !scope.projectName && resolution.projectId === scope.projectId;
        const adoptProfile = scope.projectId != null && scope.profileId == null && resolution.profileId != null;
        const nameProfile =
            scope.profileId != null && !scope.profileName && resolution.profileId === scope.profileId;

        if (!nameProject && !adoptProfile && !nameProfile) {
            return;
        }
        if (scope.projectId != null && resolution.projectId !== scope.projectId) {
            return;
        }

        setScope({
            ...scope,
            projectName: nameProject ? (resolution.projectName ?? null) : scope.projectName,
            profileId: adoptProfile ? (resolution.profileId ?? null) : scope.profileId,
            profileName:
                adoptProfile || nameProfile ? (resolution.profileName ?? null) : scope.profileName,
        });
    }, [scope, setScope, resolution]);
}

/** Edits always target the most specific selection, so the pickers alone define where a save lands. */
export function editScopeOf(scope: ScopeState): EditScope {
    if (scope.projectId != null) {
        return 'project';
    }
    return scope.profileId != null ? 'profile' : 'global';
}

export function languageLabel(languageCode: string | null | undefined): string {
    return languageCode || 'Any language';
}

/** Overrides sort project-scoped first, matching the resolution precedence the list is meant to convey. */
export function sortOverrides<T extends EmailContentOverride | EmailLayoutOverride>(overrides: T[]): T[] {
    return [...overrides].sort((a, b) => {
        const scopeRank = (x: T) => (x.projectId ? 0 : 1);
        if (scopeRank(a) !== scopeRank(b)) {
            return scopeRank(a) - scopeRank(b);
        }
        const byName = (a.scopeName ?? '').localeCompare(b.scopeName ?? '');
        if (byName !== 0) {
            return byName;
        }
        return (a.languageCode ?? '').localeCompare(b.languageCode ?? '');
    });
}
